<script>
import _ from "lodash";
import draggable from "vuedraggable";
import navigation from "../components/global/navigation.vue";
import ArtworkIntakeUpsell from "../components/artwork-intake/ArtworkIntakeUpsell.vue";

export default {
  name: "ArtworkIntakeSettings",
  components: { navigation, ArtworkIntakeUpsell, draggable },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      settings: null,
      customFields: [],
      teams: [],
      users: [],
      selectedIds: [],
      requiredIds: [],
      requireFile: false,
      recipients: [],
      isPickerOpen: false,
      isFieldPickerOpen: false,
      copiedKey: null,
      reminderFrequency: "None",
      reminderTimeOfDay: "09:00",
      reminderTimeZone: "America/Chicago",
      dayTokens: [
        { token: "Sun", label: "Sun" },
        { token: "Mon", label: "Mon" },
        { token: "Tue", label: "Tue" },
        { token: "Wed", label: "Wed" },
        { token: "Tr",  label: "Thu" },
        { token: "Fri", label: "Fri" },
        { token: "Sat", label: "Sat" },
      ],
      timeZoneOptions: [
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "America/Phoenix",
        "America/Anchorage",
        "Pacific/Honolulu",
        "America/Toronto",
        "America/Mexico_City",
        "America/Sao_Paulo",
        "Europe/London",
        "Europe/Paris",
        "Europe/Berlin",
        "Europe/Madrid",
        "Europe/Amsterdam",
        "Europe/Stockholm",
        "Europe/Istanbul",
        "Africa/Johannesburg",
        "Asia/Dubai",
        "Asia/Kolkata",
        "Asia/Singapore",
        "Asia/Hong_Kong",
        "Asia/Shanghai",
        "Asia/Tokyo",
        "Asia/Seoul",
        "Australia/Sydney",
        "Pacific/Auckland",
        "UTC",
      ],
    };
  },
  computed: {
    isPremiumAccount() {
      const sku = _.get(this, "$A.Auth.account.subscriptionState.sku", "");
      return sku.includes("standard") || sku.includes("premium") || sku.includes("enterprise");
    },
    fieldOptions() {
      return this.customFields.map((f) => ({
        value: f.id,
        text: f.label || `Field ${f.id}`,
      }));
    },
    fieldLabelMap() {
      const m = new Map();
      for (const f of this.fieldOptions) m.set(f.value, f.text);
      return m;
    },
    // Fields currently on the form, in display order (this order is what gets saved).
    selectedFields() {
      return this.selectedIds.map((id) => ({
        value: id,
        text: this.fieldLabelMap.get(id) || `Field ${id}`,
      }));
    },
    // Account custom fields not yet added to the form.
    availableFields() {
      const selected = new Set(this.selectedIds);
      return this.fieldOptions.filter((f) => !selected.has(f.value));
    },
    isFieldsDirty() {
      if (!this.settings) return false;
      // Order-sensitive: reordering fields is itself a saveable change.
      const current = (this.settings.selectedCustomFieldIds || []).join(",");
      const next = this.selectedIds.join(",");
      if (current !== next) return true;
      const currentRequired = [...(this.settings.requiredCustomFieldIds || [])].sort().join(",");
      const nextRequired = [...this.requiredIds].sort().join(",");
      return currentRequired !== nextRequired || !!this.settings.requireFile !== this.requireFile;
    },
    userMap() {
      const m = new Map();
      for (const u of this.users) m.set(u.id, u);
      return m;
    },
    teamMap() {
      const m = new Map();
      for (const t of this.teams) m.set(t.id, t);
      return m;
    },
    selectedUserIds() {
      return new Set(this.recipients.filter((r) => r.userId != null).map((r) => r.userId));
    },
    selectedTeamIds() {
      return new Set(this.recipients.filter((r) => r.teamId != null).map((r) => r.teamId));
    },
    pickerUsers() {
      return this.users
        .filter((u) => !this.selectedUserIds.has(u.id))
        .map((u) => ({ id: u.id, name: u.name || u.email || `User ${u.id}`, email: u.email || "" }));
    },
    pickerTeams() {
      return this.teams
        .filter((t) => !this.selectedTeamIds.has(t.id))
        .map((t) => ({ id: t.id, name: t.name || `Team ${t.id}`, memberCount: Array.isArray(t.Users) ? t.Users.length : (t.memberCount || 0) }));
    },
    resolvedRecipients() {
      return this.recipients.map((r, index) => {
        if (r.userId != null) {
          const u = this.userMap.get(r.userId);
          if (!u) return { index, kind: "missing", name: `Removed user (id: ${r.userId})`, secondary: "" };
          return { index, kind: "user", name: u.name || u.email || `User ${r.userId}`, secondary: u.email || "" };
        }
        const t = this.teamMap.get(r.teamId);
        if (!t) return { index, kind: "missing", name: `Removed team (id: ${r.teamId})`, secondary: "" };
        const count = Array.isArray(t.Users) ? t.Users.length : (t.memberCount || 0);
        return { index, kind: "team", name: t.name || `Team ${r.teamId}`, secondary: `${count} ${count === 1 ? "member" : "members"}` };
      });
    },
    hasAnyAccountUsersOrTeams() {
      return this.users.length > 0 || this.teams.length > 0;
    },
    canAddMore() {
      return this.pickerUsers.length > 0 || this.pickerTeams.length > 0;
    },
    previewFormUrl() {
      if (!this.settings || !this.settings.formId) return "";
      return `${window.location.origin}/artwork-intake-form/${this.settings.formId}`;
    },
    previewEmbedUrl() {
      if (!this.settings || !this.settings.formId) return "";
      return `${window.location.origin}/artwork-intake-embed-test.html?formId=${this.settings.formId}`;
    },
    isRecipientsDirty() {
      if (!this.settings) return false;
      const key = (list) =>
        list
          .map((r) => (r.userId != null ? `u${r.userId}` : `t${r.teamId}`))
          .sort()
          .join(",");
      return key(this.settings.emailRecipients || []) !== key(this.recipients);
    },
    selectedDayTokens: {
      get() {
        if (!this.reminderFrequency || this.reminderFrequency === "None") return [];
        return this.reminderFrequency.split(",").map((t) => t.trim()).filter(Boolean);
      },
      set(tokens) {
        const valid = this.dayTokens.map((d) => d.token);
        const ordered = valid.filter((t) => tokens.includes(t));
        this.reminderFrequency = ordered.length ? ordered.join(",") : "None";
      },
    },
    isReminderDirty() {
      if (!this.settings) return false;
      return (
        (this.settings.reminderFrequency || "None") !== this.reminderFrequency ||
        (this.settings.reminderTimeOfDay || "09:00") !== this.reminderTimeOfDay ||
        (this.settings.reminderTimeZone || "America/Chicago") !== this.reminderTimeZone
      );
    },
  },
  methods: {
    async copy(text, key) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedKey = key;
        setTimeout(() => {
          if (this.copiedKey === key) this.copiedKey = null;
        }, 2000);
      } catch (e) {
        console.error("Clipboard copy failed:", e);
      }
    },
    resetFields() {
      if (!this.settings) return;
      this.selectedIds = [...(this.settings.selectedCustomFieldIds || [])];
      this.requiredIds = [...(this.settings.requiredCustomFieldIds || [])];
      this.requireFile = !!this.settings.requireFile;
      this.isFieldPickerOpen = false;
    },
    toggleFieldPicker() {
      this.isFieldPickerOpen = !this.isFieldPickerOpen;
    },
    addField(id) {
      if (this.selectedIds.includes(id)) return;
      // New fields append to the end of the display order.
      this.selectedIds.push(id);
      this.isFieldPickerOpen = false;
    },
    removeField(id) {
      this.selectedIds = this.selectedIds.filter((x) => x !== id);
      this.requiredIds = this.requiredIds.filter((x) => x !== id);
    },
    toggleRequired(id) {
      if (this.requiredIds.includes(id)) {
        this.requiredIds = this.requiredIds.filter((x) => x !== id);
      } else {
        this.requiredIds.push(id);
      }
    },
    resetRecipients() {
      if (!this.settings) return;
      this.recipients = (this.settings.emailRecipients || []).map((r) => ({ ...r }));
      this.isPickerOpen = false;
    },
    resetReminder() {
      if (!this.settings) return;
      this.reminderFrequency = this.settings.reminderFrequency || "None";
      this.reminderTimeOfDay = this.settings.reminderTimeOfDay || "09:00";
      this.reminderTimeZone = this.settings.reminderTimeZone || "America/Chicago";
    },
    toggleDay(token) {
      const set = new Set(this.selectedDayTokens);
      if (set.has(token)) set.delete(token); else set.add(token);
      this.selectedDayTokens = Array.from(set);
    },
    togglePicker() {
      this.isPickerOpen = !this.isPickerOpen;
    },
    addUserRecipient(userId) {
      if (this.selectedUserIds.has(userId)) return;
      this.recipients.push({ userId, teamId: null, label: `User #${userId}` });
      this.isPickerOpen = false;
    },
    addTeamRecipient(teamId) {
      if (this.selectedTeamIds.has(teamId)) return;
      this.recipients.push({ userId: null, teamId, label: `Team #${teamId}` });
      this.isPickerOpen = false;
    },
    removeRecipient(index) {
      this.recipients.splice(index, 1);
    },
    async save() {
      this.isSaving = true;
      try {
        const payload = {
          selectedCustomFieldIds: this.selectedIds,
          requiredCustomFieldIds: this.requiredIds,
          requireFile: this.requireFile,
          emailRecipients: this.recipients.map((r) =>
            r.userId != null ? { userId: r.userId } : { teamId: r.teamId }
          ),
          reminderFrequency: this.reminderFrequency,
          reminderTimeOfDay: this.reminderTimeOfDay,
          reminderTimeZone: this.reminderTimeZone,
        };
        this.settings = await this.$A.ArtworkIntakeService.saveFormSettings(payload);
        this.selectedIds = [...(this.settings.selectedCustomFieldIds || [])];
        this.requiredIds = [...(this.settings.requiredCustomFieldIds || [])];
        this.requireFile = !!this.settings.requireFile;
        this.recipients = (this.settings.emailRecipients || []).map((r) => ({ ...r }));
        this.reminderFrequency = this.settings.reminderFrequency || "None";
        this.reminderTimeOfDay = this.settings.reminderTimeOfDay || "09:00";
        this.reminderTimeZone = this.settings.reminderTimeZone || "America/Chicago";
      } catch (e) {
        console.error("Failed to save settings:", e);
      }
      this.isSaving = false;
    },
  },
  async mounted() {
    if (!this.isPremiumAccount) {
      this.isLoading = false;
      return;
    }
    try {
      const [settings, customFields, teams, users] = await Promise.all([
        this.$A.ArtworkIntakeService.getFormSettings(),
        this.$A.AccountService.GetAllCustomProofProperties(),
        this.$A.TeamService.GetAccountTeams().catch(() => []),
        this.$A.AccountService.All().catch(() => []),
      ]);
      this.settings = settings;
      this.selectedIds = [...(settings.selectedCustomFieldIds || [])];
      this.requiredIds = [...(settings.requiredCustomFieldIds || [])];
      this.requireFile = !!settings.requireFile;
      this.recipients = (settings.emailRecipients || []).map((r) => ({ ...r }));
      this.reminderFrequency = settings.reminderFrequency || "None";
      this.reminderTimeOfDay = settings.reminderTimeOfDay || "09:00";
      this.reminderTimeZone = settings.reminderTimeZone || "America/Chicago";
      this.customFields = Array.isArray(customFields) ? customFields : [];
      this.teams = Array.isArray(teams) ? teams : [];
      this.users = Array.isArray(users) ? users : [];
    } catch (e) {
      console.error("Failed to load settings:", e);
    }
    this.isLoading = false;
  },
};
</script>

<template>
  <div class="bg-white nav-margin">
    <navigation />
    <ArtworkIntakeUpsell v-if="!isPremiumAccount" />
    <div v-else>
      <div class="back-btn bg-white border-bottom border-gray-100 d-flex gap-12 justify-content-between flex-wrap gap-2 py-3 px-3">
        <a class="p-0 d-flex align-items-center gap-1 fs-14" href="/artwork-intake">
          <img src="/img/chevron-left-gray.svg" alt="chevron-left" />
          Back
        </a>
      </div>

      <main v-if="!isLoading" class="bg-white flex-fill overflow-auto">
        <div class="settings-container mx-auto py-32">
          <div class="mb-10">
            <h6 class="text-gray-900 fw-semibold mb-2 fs-18">Form Settings</h6>
            <p class="settings-subheading fw-normal pb-3 text-gray-500 fs-12">
              Configure what your customers see on the intake form and where their submissions go.
            </p>
          </div>

          <div class="d-flex flex-column settings-carts">
            <!-- Form Fields -->
            <section class="settings-item bg-ashore-white">
              <div class="settings-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                <div class="d-flex align-items-center gap-2">
                  <div class="icon">
                    <img src="/img/list.svg" width="16" alt="form-fields-icon" />
                  </div>
                  <div class="fs-14 font-weight-bold text-gray-900">Form Fields</div>
                </div>
              </div>

              <div class="settings-body p-12 d-flex flex-column gap-2">
                <p class="fs-12 text-gray-500 mb-2">
                  Choose which of your proof custom fields appear on the intake form, and drag to set the order your customers see them in.
                </p>

                <div v-if="!fieldOptions.length" class="empty-state bg-white border rounded p-12 text-center">
                  <p class="fs-13 text-gray-700 mb-1">No custom fields yet.</p>
                  <p class="fs-12 text-gray-500 mb-2">
                    Add fields under Account Settings &rarr; Custom Fields to show them on this form.
                  </p>
                  <a class="fs-12" href="/account">Go to Account Settings</a>
                </div>

                <template v-else>
                  <div v-if="!selectedFields.length" class="muted-empty fs-12 text-gray-500">
                    No fields on the form yet — add fields below to show them on the intake form.
                  </div>

                  <draggable
                    v-else
                    :list="selectedIds"
                    handle=".field-drag-handle"
                    ghost-class="field-row--ghost"
                    class="d-flex flex-column gap-2"
                  >
                    <div
                      v-for="f in selectedFields"
                      :key="f.value"
                      class="field-row bg-white border p-12 rounded d-flex align-items-center gap-2 mb-0"
                    >
                      <span
                        class="field-drag-handle text-gray-400"
                        aria-label="Drag to reorder"
                        title="Drag to reorder"
                      >⠿</span>
                      <span class="fs-14 text-gray-900 flex-grow-1 text-truncate">{{ f.text }}</span>
                      <label class="d-flex align-items-center gap-1 mb-0 fs-12 text-gray-500">
                        <input
                          type="checkbox"
                          class="field-checkbox"
                          :checked="requiredIds.includes(f.value)"
                          @change="toggleRequired(f.value)"
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        class="btn btn-sm btn-link text-gray-500 p-0 px-2"
                        :aria-label="'Remove ' + f.text"
                        @click="removeField(f.value)"
                      >
                        ×
                      </button>
                    </div>
                  </draggable>

                  <div class="position-relative">
                    <button
                      type="button"
                      class="btn btn-sm bg-gray-50 button-dark"
                      :disabled="!availableFields.length"
                      @click="toggleFieldPicker"
                    >
                      + Add field
                    </button>

                    <div v-if="isFieldPickerOpen" class="recipient-picker shadow-sm bg-white border rounded p-12">
                      <div class="d-flex flex-column gap-1">
                        <button
                          v-for="f in availableFields"
                          :key="'af' + f.value"
                          type="button"
                          class="picker-option text-start"
                          @click="addField(f.value)"
                        >
                          <span class="fs-14 text-gray-900">{{ f.text }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>

                <label class="d-flex align-items-center gap-2 mb-0 mt-2 fs-14 text-gray-900">
                  <input type="checkbox" class="field-checkbox" v-model="requireFile" />
                  Require at least one file upload
                </label>
              </div>

              <div
                v-if="isFieldsDirty"
                class="settings-footer d-flex justify-content-end gap-2 p-12 border-top border-gray-100"
              >
                <button class="btn btn-sm bg-gray-50 button-dark" :disabled="isSaving" @click="resetFields">
                  Cancel
                </button>
                <button class="btn btn-success btn-sm" :disabled="isSaving" @click="save">
                  {{ isSaving ? "Saving..." : "Save changes" }}
                </button>
              </div>
            </section>

            <!-- Email notifications -->
            <section class="settings-item bg-ashore-white">
              <div class="settings-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                <div class="d-flex align-items-center gap-2">
                  <div class="icon">
                    <img src="/img/users.svg" width="16" alt="recipients-icon" />
                  </div>
                  <div class="fs-14 font-weight-bold text-gray-900">Email notifications</div>
                </div>
              </div>

              <div class="settings-body p-12 d-flex flex-column gap-3">
                <p class="fs-12 text-gray-500 mb-0">
                  Recipients receive an email whenever someone submits this form.
                </p>

                <div v-if="!hasAnyAccountUsersOrTeams" class="empty-state bg-white border rounded p-12 text-center">
                  <p class="fs-13 text-gray-700 mb-1">No teammates or teams yet.</p>
                  <p class="fs-12 text-gray-500 mb-2">
                    Add users or create a team to route submission notifications.
                  </p>
                  <a class="fs-12" href="/users">Manage users &amp; teams</a>
                </div>

                <template v-else>
                  <div v-if="!recipients.length" class="muted-empty fs-12 text-gray-500">
                    No recipients yet — add users or teams to be notified by email when this form is submitted.
                  </div>

                  <div v-else class="d-flex flex-column gap-2">
                    <div
                      v-for="row in resolvedRecipients"
                      :key="row.kind + '-' + row.index"
                      class="recipient-row bg-white border p-12 rounded d-flex align-items-center gap-2"
                    >
                      <div class="recipient-avatar" :class="'is-' + row.kind">
                        <img v-if="row.kind === 'team'" src="/img/users.svg" width="14" alt="" />
                        <span v-else>{{ (row.name || "?").charAt(0).toUpperCase() }}</span>
                      </div>
                      <div class="d-flex flex-column flex-grow-1 min-width-0">
                        <span class="fs-14 text-gray-900 text-truncate">{{ row.name }}</span>
                        <span v-if="row.secondary" class="fs-12 text-gray-500 text-truncate">{{ row.secondary }}</span>
                      </div>
                      <button
                        type="button"
                        class="btn btn-sm btn-link text-gray-500 p-0 px-2"
                        :aria-label="'Remove ' + row.name"
                        @click="removeRecipient(row.index)"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div class="position-relative">
                    <button
                      type="button"
                      class="btn btn-sm bg-gray-50 button-dark"
                      :disabled="!canAddMore"
                      @click="togglePicker"
                    >
                      + Add recipient
                    </button>

                    <div v-if="isPickerOpen" class="recipient-picker shadow-sm bg-white border rounded p-12">
                      <div v-if="pickerUsers.length" class="d-flex flex-column gap-1 mb-2">
                        <div class="fs-12 fw-semibold text-gray-700 mb-1">Users</div>
                        <button
                          v-for="u in pickerUsers"
                          :key="'pu' + u.id"
                          type="button"
                          class="picker-option text-start"
                          @click="addUserRecipient(u.id)"
                        >
                          <span class="fs-14 text-gray-900">{{ u.name }}</span>
                          <span v-if="u.email" class="fs-12 text-gray-500">{{ u.email }}</span>
                        </button>
                      </div>
                      <div v-if="pickerTeams.length" class="d-flex flex-column gap-1">
                        <div class="fs-12 fw-semibold text-gray-700 mb-1">Teams</div>
                        <button
                          v-for="t in pickerTeams"
                          :key="'pt' + t.id"
                          type="button"
                          class="picker-option text-start"
                          @click="addTeamRecipient(t.id)"
                        >
                          <span class="fs-14 text-gray-900">{{ t.name }}</span>
                          <span class="fs-12 text-gray-500">{{ t.memberCount }} {{ t.memberCount === 1 ? "member" : "members" }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div
                v-if="isRecipientsDirty"
                class="settings-footer d-flex justify-content-end gap-2 p-12 border-top border-gray-100"
              >
                <button class="btn btn-sm bg-gray-50 button-dark" :disabled="isSaving" @click="resetRecipients">
                  Cancel
                </button>
                <button class="btn btn-success btn-sm" :disabled="isSaving" @click="save">
                  {{ isSaving ? "Saving..." : "Save changes" }}
                </button>
              </div>
            </section>

            <!-- Submitter reminders -->
            <section class="settings-item bg-ashore-white">
              <div class="settings-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                <div class="d-flex align-items-center gap-2">
                  <div class="icon">
                    <img src="/img/bell.svg" width="16" alt="reminders-icon" onerror="this.style.display='none'" />
                  </div>
                  <div class="fs-14 font-weight-bold text-gray-900">Default approval reminders for new contacts</div>
                </div>
              </div>

              <div class="settings-body p-12 d-flex flex-column gap-3">
                <p class="fs-12 text-gray-500 mb-0">
                  When someone submits this form, a new contact is created. These settings set the default reminder schedule that contact will receive once they're added as an approver on a proof — they don't affect the intake form itself.
                </p>

                <div class="d-flex flex-column gap-2">
                  <label class="fs-12 fw-semibold text-gray-700 mb-0">Days to send</label>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      v-for="d in dayTokens"
                      :key="d.token"
                      type="button"
                      class="day-chip"
                      :class="{ 'day-chip--active': selectedDayTokens.includes(d.token) }"
                      @click="toggleDay(d.token)"
                    >
                      {{ d.label }}
                    </button>
                  </div>
                  <p class="fs-12 text-gray-500 mb-0">
                    Leave all unchecked to disable reminders.
                  </p>
                </div>

                <div class="d-flex flex-wrap gap-3">
                  <div class="d-flex flex-column gap-1 flex-grow-1" style="min-width: 180px;">
                    <label class="fs-12 fw-semibold text-gray-700 mb-0" for="reminder-time">Time of day</label>
                    <input
                      id="reminder-time"
                      type="time"
                      class="form-control form-control-sm border-gray-300"
                      v-model="reminderTimeOfDay"
                    />
                  </div>
                  <div class="d-flex flex-column gap-1 flex-grow-1" style="min-width: 240px;">
                    <label class="fs-12 fw-semibold text-gray-700 mb-0" for="reminder-tz">Time zone</label>
                    <select
                      id="reminder-tz"
                      class="form-control form-control-sm border-gray-300"
                      v-model="reminderTimeZone"
                    >
                      <option v-for="tz in timeZoneOptions" :key="tz" :value="tz">{{ tz }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div
                v-if="isReminderDirty"
                class="settings-footer d-flex justify-content-end gap-2 p-12 border-top border-gray-100"
              >
                <button class="btn btn-sm bg-gray-50 button-dark" :disabled="isSaving" @click="resetReminder">
                  Cancel
                </button>
                <button class="btn btn-success btn-sm" :disabled="isSaving" @click="save">
                  {{ isSaving ? "Saving..." : "Save changes" }}
                </button>
              </div>
            </section>

            <!-- Public Link -->
            <section class="settings-item bg-ashore-white">
              <div class="settings-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                <div class="d-flex align-items-center gap-2">
                  <div class="icon">
                    <img src="/img/link.svg" width="16" alt="link-icon" />
                  </div>
                  <div class="fs-14 font-weight-bold text-gray-900">Public Link</div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <a
                    v-if="previewFormUrl"
                    class="btn btn-sm bg-gray-50 button-dark"
                    :href="previewFormUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    Preview
                  </a>
                  <button
                    class="btn btn-success btn-sm"
                    :disabled="!settings || !settings.publicFormUrl"
                    @click="copy(settings.publicFormUrl, 'url')"
                  >
                    {{ copiedKey === "url" ? "Copied" : "Copy link" }}
                  </button>
                </div>
              </div>

              <div class="settings-body p-12 d-flex flex-column gap-2">
                <p class="fs-12 text-gray-500 mb-2">
                  Send this link to your customers, or link to it from your website.
                </p>

                <div v-if="!settings || !settings.publicFormUrl" class="empty-state bg-white border rounded p-12 text-center">
                  <p class="fs-13 text-gray-700 mb-1">Public link not available yet.</p>
                  <p class="fs-12 text-gray-500 mb-0">
                    Save your form settings to generate a shareable link.
                  </p>
                </div>

                <input
                  v-else
                  type="text"
                  class="form-control form-control-sm border-gray-300 fw-normal text-gray-600"
                  :value="settings.publicFormUrl"
                  readonly
                />
              </div>
            </section>

            <!-- Embed Code -->
            <section class="settings-item bg-ashore-white">
              <div class="settings-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                <div class="d-flex align-items-center gap-2">
                  <div class="icon">
                    <img src="/img/share.svg" width="16" alt="embed-icon" />
                  </div>
                  <div class="fs-14 font-weight-bold text-gray-900">Embed Code</div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <a
                    v-if="previewEmbedUrl"
                    class="btn btn-sm bg-gray-50 button-dark"
                    :href="previewEmbedUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    Preview embed
                  </a>
                  <button
                    class="btn btn-success btn-sm"
                    :disabled="!settings || !settings.embedSnippet"
                    @click="copy(settings.embedSnippet, 'embed')"
                  >
                    {{ copiedKey === "embed" ? "Copied" : "Copy code" }}
                  </button>
                </div>
              </div>

              <div class="settings-body p-12 d-flex flex-column gap-2">
                <p class="fs-12 text-gray-500 mb-2">
                  Paste this snippet into your website to embed the form.
                </p>

                <div v-if="!settings || !settings.embedSnippet" class="empty-state bg-white border rounded p-12 text-center">
                  <p class="fs-13 text-gray-700 mb-1">Embed snippet not available yet.</p>
                  <p class="fs-12 text-gray-500 mb-0">
                    Save your form settings to generate the embed code.
                  </p>
                </div>

                <textarea
                  v-else
                  class="form-control form-control-sm border-gray-300 fw-normal text-gray-600 embed-textarea"
                  :value="settings.embedSnippet"
                  readonly
                  rows="3"
                />
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.settings-container {
  max-width: 720px;
}

.settings-subheading {
  border-bottom: 1px solid #F4F4F4 !important;
}

.back-btn a {
  color: #909092;
}

.settings-carts {
  gap: 18px;
}

.settings-heading {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.settings-body {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.settings-item {
  border: 1px solid #EBEBED;
  border-radius: 8px;
}

.field-row {
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease;
}
.field-row:hover {
  border-color: #cfd6df !important;
  background-color: #fafbfc !important;
}
.field-row:has(.field-checkbox:checked) {
  border-color: rgba(1, 146, 86, 0.45) !important;
  background-color: rgba(1, 146, 86, 0.04) !important;
}
.field-row:focus-within {
  border-color: rgba(1, 146, 86, 0.6) !important;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.14);
}

.field-drag-handle {
  cursor: grab;
  font-size: 16px;
  line-height: 1;
  user-select: none;
  flex: 0 0 auto;
}
.field-drag-handle:active {
  cursor: grabbing;
}
.field-row--ghost {
  opacity: 0.5;
  border-color: rgba(1, 146, 86, 0.45) !important;
  background-color: rgba(1, 146, 86, 0.04) !important;
}

.field-checkbox {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  margin: 0;
  border: 1.5px solid #cfd6df;
  border-radius: 5px;
  background-color: #ffffff;
  cursor: pointer;
  position: relative;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease;
}
.field-checkbox:hover {
  border-color: #019256;
}
.field-checkbox:focus-visible {
  outline: none;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.2);
}
.field-checkbox:checked {
  background-color: #019256;
  border-color: #019256;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='white' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'><polyline points='3.5 8.5 6.8 11.5 12.5 5'/></svg>");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px 14px;
}
.field-checkbox:checked:hover {
  background-color: #017a48;
  border-color: #017a48;
}
.field-checkbox:disabled {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
  cursor: not-allowed;
}

.team-select {
  max-width: 360px;
}

.recipient-row {
  transition: border-color 120ms ease, background-color 120ms ease;
}
.recipient-row:hover {
  border-color: #cfd6df !important;
}

.recipient-avatar {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #eef2f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
}
.recipient-avatar.is-team {
  background-color: #e6f4ee;
}

.recipient-picker {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  min-width: 280px;
  max-height: 320px;
  overflow-y: auto;
}

.picker-option {
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  cursor: pointer;
}
.picker-option:hover {
  background-color: #f5f7fa;
}
.picker-option:focus-visible {
  outline: none;
  background-color: #f5f7fa;
  box-shadow: 0 0 0 2px rgba(1, 146, 86, 0.2);
}

.min-width-0 {
  min-width: 0;
}

.embed-textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

.day-chip {
  border: 1px solid #cfd6df;
  background-color: #ffffff;
  color: #4b5563;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease, color 120ms ease;
}
.day-chip:hover {
  border-color: #019256;
}
.day-chip--active {
  border-color: #019256;
  background-color: rgba(1, 146, 86, 0.08);
  color: #019256;
}
.day-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.2);
}

.empty-state a {
  color: #019256;
  font-weight: 600;
}

.mb-10 {
  margin-bottom: 10px !important;
}

.fs-18 {
  font-size: 18px !important;
}

.fs-13 {
  font-size: 13px !important;
}

@media (max-width: 992px) {
  .settings-container {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
